import { CalendarDays, History, ShieldCheck, Sparkles } from "lucide-react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const DURATION_OPTIONS = [3, 6, 12, 24];

export function LaunchPricingPolicyPanel({ history, policy, setValue }) {
  const erpSlabs = policy.erp_slabs || [];
  const onboardingSlabs = policy.onboarding_slabs || [];
  const gst = policy.gst_treatment || {};
  const overrides = policy.authorized_overrides || {};

  return (
    <section className="space-y-4" data-testid="launch-policy-admin-panel">
      <PolicySection
        description={
          "This policy drives public quotation previews only. It does not activate subscriptions " +
          "or create financial records."
        }
        icon={Sparkles}
        title="Budget Launch Pricing Policy"
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <ReadOnlyField
            label="Policy version"
            value={policy.policy_version || "Assigned on save"}
          />
          <DateField
            label="Effective from"
            onChange={(value) => setValue("pricing_policy.effective_from", value)}
            testId="launch-policy-effective-from"
            value={policy.effective_from || ""}
          />
          <DateField
            label="Offer valid until"
            onChange={(value) => setValue("pricing_policy.effective_until", value)}
            testId="launch-policy-effective-until"
            value={policy.effective_until || ""}
          />
        </div>
      </PolicySection>

      <PolicySection
        description={
          "These progressive Budget Launch rates drive public quotations only. " +
          "Legacy subscription slabs remain unchanged."
        }
        icon={Sparkles}
        title="Budget Launch ERP Slabs"
      >
        <div className="grid gap-3 sm:grid-cols-3">
          {erpSlabs.map((slab, index) => (
            <MoneyField
              key={`${slab.from}-${slab.to}`}
              label={`${slab.from}–${slab.to} units / month`}
              onChange={(value) => setValue(
                `pricing_policy.erp_slabs.${index}.rate`,
                value,
              )}
              testId={`launch-policy-erp-rate-${index}`}
              value={slab.rate}
            />
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Quotes above the final standard band always require a tailored commercial proposal.
        </p>
      </PolicySection>

      <PolicySection
        description={
          "ERP discounts are calculated only against the selected-duration ERP " +
          "subscription subtotal."
        }
        icon={CalendarDays}
        title="Duration Discounts"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {DURATION_OPTIONS.map((months) => (
            <PercentField
              key={months}
              label={`${months} months — ERP`}
              onChange={(value) => setValue(
                `pricing_policy.duration_discounts_pct.${months}`,
                value,
              )}
              testId={`launch-policy-erp-discount-${months}`}
              value={policy.duration_discounts_pct?.[months] ?? 0}
            />
          ))}
        </div>
      </PolicySection>

      <PolicySection
        description={
          "Standard onboarding is quoted for setup only. Custom migration, " +
          "reconciliation, on-site work and integrations remain separately quoted."
        }
        icon={Sparkles}
        title="Onboarding Fees & Savings"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {onboardingSlabs.map((slab, index) => (
            <MoneyField
              key={`${slab.from}-${slab.to}`}
              label={`${slab.from}–${slab.to} units`}
              onChange={(value) => setValue(
                `pricing_policy.onboarding_slabs.${index}.amount`,
                value,
              )}
              testId={`launch-policy-onboarding-fee-${index}`}
              value={slab.amount}
            />
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {DURATION_OPTIONS.map((months) => (
            <PercentField
              key={months}
              label={`${months} months — onboarding`}
              onChange={(value) => setValue(
                `pricing_policy.onboarding_discounts_pct.${months}`,
                value,
              )}
              testId={`launch-policy-onboarding-discount-${months}`}
              value={policy.onboarding_discounts_pct?.[months] ?? 0}
            />
          ))}
        </div>
      </PolicySection>

      <PolicySection
        description={
          "The public quote inherits the existing Society GST status and rate. These switches " +
          "specify which quoted amounts are taxable when GST is enabled."
        }
        icon={ShieldCheck}
        title="GST Treatment & Authorized Overrides"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <ToggleField
            checked={Boolean(gst.include_resident_portal)}
            label="Include Resident Portal in GST base"
            onChange={(value) => setValue(
              "pricing_policy.gst_treatment.include_resident_portal",
              value,
            )}
            testId="launch-policy-gst-resident-portal"
          />
          <ToggleField
            checked={Boolean(gst.include_onboarding)}
            label="Include onboarding in GST base"
            onChange={(value) => setValue(
              "pricing_policy.gst_treatment.include_onboarding",
              value,
            )}
            testId="launch-policy-gst-onboarding"
          />
        </div>
        <div className="mt-5 border-t border-slate-200 pt-5">
          <ToggleField
            checked={Boolean(overrides.enabled)}
            label="Allow authorized commercial overrides"
            onChange={(value) => setValue(
              "pricing_policy.authorized_overrides.enabled",
              value,
            )}
            testId="launch-policy-overrides-enabled"
          />
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <PercentField
              label="Maximum ERP override"
              onChange={(value) => setValue(
                "pricing_policy.authorized_overrides.max_erp_discount_pct",
                value,
              )}
              testId="launch-policy-max-erp-override"
              value={overrides.max_erp_discount_pct ?? 20}
            />
            <PercentField
              label="Maximum onboarding override"
              onChange={(value) => setValue(
                "pricing_policy.authorized_overrides.max_onboarding_discount_pct",
                value,
              )}
              testId="launch-policy-max-onboarding-override"
              value={overrides.max_onboarding_discount_pct ?? 100}
            />
            <ToggleField
              checked={Boolean(overrides.reason_required)}
              label="Reason required"
              onChange={(value) => setValue(
                "pricing_policy.authorized_overrides.reason_required",
                value,
              )}
              testId="launch-policy-override-reason"
            />
          </div>
        </div>
      </PolicySection>

      <PolicySection
        description={
          "Historical policy snapshots remain read-only. They never rewrite an issued invoice or " +
          "contracted subscription term."
        }
        icon={History}
        title="Policy History"
      >
        <div className="divide-y divide-slate-100" data-testid="launch-policy-history">
          {history.length === 0 && (
            <p
              className="py-3 text-sm text-muted-foreground"
              data-testid="launch-policy-history-empty"
            >
              No launch-policy snapshot has been saved yet.
            </p>
          )}
          {history.map((item, index) => (
            <div
              className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm"
              data-testid={`launch-policy-history-row-${index}`}
              key={`${item.created_at}-${index}`}
            >
              <span className="font-semibold text-slate-800">
                {item.policy_version || "Earlier commercial settings"}
              </span>
              <span className="text-slate-500">
                Effective {item.effective_date || "Not recorded"}
              </span>
            </div>
          ))}
        </div>
      </PolicySection>
    </section>
  );
}

function PolicySection({ children, description, icon: Icon, title }) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="mb-4 flex items-start gap-3">
        <span
          className={[
            "flex h-9 w-9 items-center justify-center rounded-lg",
            "bg-orange-50 text-orange-600",
          ].join(" ")}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function DateField({ label, onChange, testId, value }) {
  return (
    <div>
      <Label className="mb-1 block text-xs text-muted-foreground">{label}</Label>
      <Input
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        type="date"
        value={value}
      />
    </div>
  );
}

function MoneyField({ label, onChange, testId, value }) {
  return (
    <div>
      <Label className="mb-1 block text-xs text-muted-foreground">{label}</Label>
      <Input
        data-testid={testId}
        min="0"
        onChange={(event) => onChange(toNumber(event.target.value))}
        type="number"
        value={value ?? 0}
      />
    </div>
  );
}

function PercentField({ label, onChange, testId, value }) {
  return (
    <div>
      <Label className="mb-1 block text-xs text-muted-foreground">{label}</Label>
      <Input
        data-testid={testId}
        max="100"
        min="0"
        onChange={(event) => onChange(toNumber(event.target.value))}
        type="number"
        value={value ?? 0}
      />
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-2 text-sm font-semibold" data-testid="launch-policy-version">{value}</p>
    </div>
  );
}

function ToggleField({ checked, label, onChange, testId }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-3">
      <Label className="text-sm font-medium">{label}</Label>
      <Switch checked={checked} data-testid={testId} onCheckedChange={onChange} />
    </div>
  );
}

function toNumber(value) {
  return value === "" ? 0 : Number(value);
}